import React from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  CircularProgress
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useTextTranslation } from '../../../Hooks';
import clsx from 'clsx';

const useStyles = makeStyles((theme) => ({
  button: {
    fontSize: '1.6rem',
    padding: '.8rem 1.6rem',
    textTransform: 'none',
    borderRadius: '0.7rem',
    textDecoration: 'none',
    zIndex: 1
  },
  primary: {
    background: theme.palette.styleguide.yellow,
    color: '#fff',
    fontWeight: 'bold',
    '&:hover': {
      background: '#f09e15'
    }
  },
  secondary: {
    background: theme.palette.styleguide.beige,
    color: theme.palette.styleguide.maroon,
    borderColor: theme.palette.styleguide.maroon,
    borderStyle: 'solid',
    borderWidth: 1,
    '&:hover': {
      background: theme.palette.common.lightGrey
    }
  },
  warning: {
    background: theme.palette.styleguide.red,
    color: '#fff',
    fontWeight: 'bold',
    '&:hover': {
      background: '#802924'
    }
  },
  disabled: {
    backgroundColor: '#ccc !important'
  },
  loading: {
    // Keep the button's own colour while it's busy. We can't use the native
    // `disabled` attribute (MUI would repaint it with a translucent grey that
    // vanishes on coloured backgrounds), so block interaction via pointer-events.
    pointerEvents: 'none'
  }
}));

const StoreButton = ({ label, disabled, loading, onClick, variant, color, fullWidth, type, dataTestId }) => {
  const classes = useStyles();
  const translatedLabel = useTextTranslation(label);

  // `loading` is the dominant state: a busy button stays its own colour with a
  // spinner and is non-interactive, even when the caller also passes `disabled`
  // (several do: `disabled={loading || ...}`). The grey disabled look only shows
  // when it's disabled and NOT loading.
  const showDisabled = disabled && !loading;

  return (
    <Button
      type={loading ? 'button' : (type || 'button')}
      data-testid={dataTestId}
      className={clsx(classes.button, classes[color || 'primary'], showDisabled && classes.disabled, loading && classes.loading)}
      variant={variant || 'contained'}
      onClick={loading ? undefined : onClick}
      fullWidth={fullWidth}
      disabled={showDisabled}
      aria-busy={loading ? true : undefined}
      aria-disabled={disabled || loading ? true : undefined}
    >
      {loading
        ? <CircularProgress size={24} color='inherit' />
        : translatedLabel}
    </Button>
  );
};

StoreButton.propTypes = {
  label: PropTypes.string.isRequired,
  onClick: PropTypes.func,
  variant: PropTypes.string,
  fullWidth: PropTypes.bool,
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  type: PropTypes.string,
  dataTestId: PropTypes.string
};

export default StoreButton;
